import { useRef, useState, type ReactNode } from "react";
import { IconPencil } from "@tabler/icons-react";
import Text from "./Text";
import Tooltip from "./Tooltip";
// @ts-expect-error pure js
import TextInputStandalone from "./TextInputStandalone";
// @ts-expect-error pure js
import { classnames } from "../../utils";
import useCanWrite from "../../hooks/use-can-write";
import { READ_ONLY_MESSAGE } from "../../utils/permissions";
import styles from "./InlineEditableText.module.css";

interface InlineEditableTextProps {
  value: string | number | null | undefined;
  /** Resolve to `false` (e.g. a failed save the caller already reported) to stay in edit mode with the user's input. */
  onSave: (value: string) => Promise<boolean | void>;
  label: string;
  format?: (value: string | number) => ReactNode;
  emptyText?: string;
  type?: "text" | "number";
  step?: string;
  unit?: string;
  placeholder?: string;
  disabled?: boolean;
  requiresWrite?: boolean;
  profileId?: number | null;
  className?: string;
}

export default function InlineEditableText({
  value,
  onSave,
  label,
  format,
  emptyText = "Non renseigné",
  type = "text",
  step,
  unit,
  placeholder,
  disabled,
  requiresWrite,
  profileId,
  className,
}: InlineEditableTextProps) {
  const [editing, setEditing] = useState(false);
  const [saving, setSaving] = useState(false);
  const [draft, setDraft] = useState("");
  // Enter, Escape and the blur that follows them must only end the edit once.
  const finishingRef = useRef(false);
  const displayRef = useRef<HTMLButtonElement>(null);
  const writable = useCanWrite(profileId);
  const blocked = requiresWrite === true && !writable;

  const initial = value === null || value === undefined ? "" : String(value);

  function startEditing() {
    finishingRef.current = false;
    setDraft(initial);
    setEditing(true);
  }

  function stopEditing(restoreFocus: boolean) {
    setEditing(false);
    if (restoreFocus) requestAnimationFrame(() => displayRef.current?.focus());
  }

  async function commit(restoreFocus: boolean) {
    if (finishingRef.current) return;
    finishingRef.current = true;
    if (draft.trim() === initial) {
      stopEditing(restoreFocus);
      return;
    }
    setSaving(true);
    try {
      if ((await onSave(draft)) === false) {
        finishingRef.current = false;
      } else {
        stopEditing(restoreFocus);
      }
    } finally {
      setSaving(false);
    }
  }

  function cancel() {
    finishingRef.current = true;
    stopEditing(true);
  }

  const isEmpty = initial === "";
  const field = (
    <div className={classnames(styles.root, className)}>
      {/* Stays in the layout while editing so the input, laid over it, never resizes its container. */}
      <button
        ref={displayRef}
        type="button"
        aria-label={`Modifier : ${label}`}
        className={classnames(styles.display, editing && styles.hidden)}
        disabled={disabled || blocked}
        onClick={startEditing}
      >
        <Text as="span" color={isEmpty ? "muted" : undefined}>
          {isEmpty ? emptyText : format ? format(value!) : initial}
        </Text>
        <IconPencil size={16} className={styles.icon} aria-hidden />
      </button>
      {editing && (
        <TextInputStandalone
          className={styles.input}
          aria-label={label}
          type={type}
          step={step}
          unit={unit}
          placeholder={placeholder}
          value={draft}
          readOnly={saving}
          autoFocus
          onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
            setDraft(e.target.value)
          }
          onKeyDown={(e: React.KeyboardEvent<HTMLInputElement>) => {
            if (e.key === "Enter") {
              e.preventDefault();
              commit(true);
            } else if (e.key === "Escape") {
              e.preventDefault();
              cancel();
            }
          }}
          onBlur={() => commit(false)}
        />
      )}
    </div>
  );

  if (blocked) return <Tooltip content={READ_ONLY_MESSAGE}>{field}</Tooltip>;
  return field;
}
